import {Injectable} from "@angular/core";
import {ReplaySubject, Subject} from "rxjs";
import {MdxMemberUtil} from "../mdx-member-util";
import {AbstractSearchModel, FilterValueChangedEvent} from "@fedeltapointofsale/data-client";
import {SearchControlSelectObject, SearchControlSelectObjects} from "../../entity/select/search-control-select-objects";
import {CubeDefinitionData} from "../cube-definition.data";
import {BIService} from "../bi.service";

export class FilterDimensionMembers {

    constructor(public name: string, public members: string[]) {
        if (!this.members) {
            this.members = [];
        }
    }
}

export class FilterMembers {
    /**
     * Selected members for filtering
     */
    public members: string[];

    /**
     * Selected date range for filtering
     */
    public dateRange: string;
}

export function provideFilterModel(): any[] {
    return [
        FilterModel,
        {provide: AbstractSearchModel, useExisting: FilterModel}
    ];
}

/**
 * Filter Model based in a AbstractSearchModel that works mapping dimension members to filter ids so they can be used in the search controls.
 */
@Injectable()
export class FilterModel extends AbstractSearchModel {

    public static readonly DATE_RANGE_FILTER_NAME = "dateRangeRef";
    public cube: CubeDefinitionData;
    /**
     * Indicates that the filter members have been updated
     */
    public filterMembersUpdated$: Subject<FilterMembers> = new ReplaySubject<FilterMembers>(1);
    /**
     * Indicates that a dimension and its values have been updated
     */
    public filterDimensionUpdated$: Subject<FilterDimensionMembers> = new Subject<FilterDimensionMembers>();
    /**
     * Indicates a list of all the available attributes
     */
    attributeFilterOptions: string[] = [];
    attributeFilterOptions$: Subject<string[]> = new ReplaySubject(1);
    /**
     * Selected date range
     */
    public dateRange: string;
    /**
     * Keep the member id values indexed by dimension id
     */
    private internalFilter: any = {};
    /**
     * Filter members indexed by dimension
     */
    private dimensionFilterMembers: Map<string, string []> = new Map();

    constructor(private biService: BIService) {
        super();
    }

    get filter(): any {
        return this.internalFilter;
    }

    /**
     * Initialises the filter. Refresh may need to be called if the initial state is required to be fetched
     * @param {string} cube Target cube
     * @param {string[]} attributes Top name dimensions that may be used for filtering
     * @param {string} selectedDateRange Applied date range filter
     * @param {string[]} selectedMembers Applied filters
     */
    public init(cube: CubeDefinitionData, selectedDateRange: string, selectedMembers: string[]): void {
        this.cube = cube;

        this.attributeFilterOptions = cube.attributes;
        this.attributeFilterOptions$.next(this.attributeFilterOptions);

        this.setInternalFilterValue(FilterModel.DATE_RANGE_FILTER_NAME, selectedDateRange);

        this.initFilterMembers(selectedMembers);

        if (!selectedMembers || selectedMembers.length === 0) {
            this.notifyFilterChange();
        }
    }

    /**
     * Configures the initial selected filters
     * @param {string[]} selectedMembers
     */
    initFilterMembers(selectedMembers: string[]): void {
        if (!selectedMembers) {
            return;
        }
        const groupedMap: Map<string, string[]> = selectedMembers.reduce((entryMap, e) => {
            const dimension: string = MdxMemberUtil.getDimension(e);
            entryMap.set(dimension, [...entryMap.get(dimension) || [], e]);
            return entryMap;
        }, new Map());

        // clear the current filter members
        this.filterMembersUpdated$.next(undefined);

        this.dimensionFilterMembers.forEach((values, dimension) => {
            this.internalFilter[dimension] = undefined;
            this.filterFieldChange.emit(new FilterValueChangedEvent(dimension, undefined));
        });
        this.dimensionFilterMembers = new Map<string, string[]>();
        this.internalFilter = {};
        this.clearFilterOptions$.next();

        if (selectedMembers.length) {
            this.biService.getMemberMap(this.cube.cubeName, selectedMembers).then((memberMap: { [key: string]: string }) => {
                groupedMap.forEach((members: string[], dimension: string) => {
                    this.dimensionFilterMembers.set(dimension, members);

                    const selectObjects: SearchControlSelectObjects = new SearchControlSelectObjects();
                    members.forEach((member: string) => {
                        const id: string = this.getDimensionValueFromMember(member);
                        let name: string = memberMap ? memberMap[member] : undefined;

                        // TODO - See POS-13418 - it would be better if the unmapped names were left unspecified and the multi-select control enhanced to force a lookup to resolved the id.
                        if (!name) {
                            name = "(" + id + ")";
                        }
                        selectObjects.objects.push(new SearchControlSelectObject(id, name));
                    });
                    this.internalFilter[dimension] = selectObjects;

                    this.filterFieldChange.emit(new FilterValueChangedEvent(dimension, this.internalFilter[dimension]));
                });
                this.notifyFilterChange();
            });
        }
    }

    /**
     * Filter for selected members
     * @param {ChartMemberSelection} e
     */
    public filterSelectedMembers(dataMembers: string[], dataLabels: string[]): void {
        if (dataMembers && dataMembers.length) {
            const dimension: string = MdxMemberUtil.getDimension(dataMembers[0]);
            const selectObjects: SearchControlSelectObjects = new SearchControlSelectObjects();
            selectObjects.notifyChange = true;
            let index = 0;
            dataMembers.forEach((member: string) => {
                selectObjects.objects.push(new SearchControlSelectObject(this.getDimensionValueFromMember(member), dataLabels[index]));
                index++;
            });
            this.setInternalFilterValue(dimension, selectObjects);
        }
    }

    /**
     * Notify that the filter members have been updated
     */
    public notifyFilterChange(): void {
        const f: FilterMembers = new FilterMembers();
        f.dateRange = this.dateRange;
        f.members = this.getFilterMembers();
        this.filterMembersUpdated$.next(f);
    }

    /**
     * Return a list of all the members
     * @returns {string[]}
     */
    public getFilterMembers(): string[] {
        let filter: string[] = [];

        this.dimensionFilterMembers.forEach((value: string[]) => {
            filter = filter.concat(value);
        });

        return filter;
    }

    /**
     * Return the filter members by dimension
     * @param {string} dimension
     * @returns {string[]}
     */
    public getFilterMembersByDimension(dimension: string): string[] {
        return this.dimensionFilterMembers.get(dimension);
    }

    override getResource(): string {
        return "bi";
    }

    override isEntityLocalisable(): boolean {
        return false;
    }

    getFilterValue(filterName: any): any {
        return this.internalFilter[filterName];
    }

    setFilterValue(filterName: string, value: any): void {
        if (this.setInternalFilterValue(filterName, value)) {
            this.notifyFilterChange();

            this.filterDimensionUpdated$.next(new FilterDimensionMembers(filterName, this.dimensionFilterMembers.get(filterName)));
        }
    }

    /**
     * Set the members for a dimension
     * @param {string} filterName
     * @param {string[]} members
     */
    setDimensionFilterMembers(filterName: string, members: string[]): void {
        if (!this.match(members, this.dimensionFilterMembers.get(filterName))) {
            if (members != undefined) {
                this.dimensionFilterMembers.set(filterName, members);
                this.internalFilter[filterName] = this.transformMembersToValues(filterName, members);
            } else {
                this.dimensionFilterMembers.delete(filterName);
                this.internalFilter[filterName] = members;
            }

            this.filterFieldChange.emit(new FilterValueChangedEvent(filterName, this.internalFilter[filterName]));

            this.notifyFilterChange();
        }
    }

    setFilterValues(filter: any): void {
        for (const filterName in filter) {
            if (filter.hasOwnProperty(filterName)) {
                this.setInternalFilterValue(filterName, filter[filterName]);
            }
        }
        this.notifyFilterChange();
    }

    /**
     * Transform values to the member format. i.e. [35, 40] to [Product].[product].[product].[35]...
     * @param {string} name
     * @param value
     * @returns {string[]}
     */
    transformValuesToMembers(name: string, value: any): string[] {
        const members: string[] = [];

        if (value instanceof SearchControlSelectObjects) {
            value.objects.forEach((object: SearchControlSelectObject) => members.push(name + ".[" + object.id + "]"));
        } else if (Array.isArray(value)) {
            value.forEach((i) => members.push(name + ".[" + i + "]"))
        } else {
            members.push(name + ".[" + value + "]");
        }

        return members;
    }

    /**
     * Transform a list of members to values. i.e. [Product].[product].[product].[35], [Product].[product].[product].[40] to [35, 50]
     * @param {string} name
     * @param {string[]} value
     * @returns {any[]}
     */
    transformMembersToValues(name: string, value: string[]): any[] {
        const values: any [] = [];
        value.forEach((member) => {
            values.push(this.getDimensionValueFromMember(member));
        });

        return values;
    }

    private getDimensionValueFromMember(member: string): any {
        const parts: string[] = MdxMemberUtil.getParts(member);
        const value: string = MdxMemberUtil.getDimensionValue(member);
        const type: string = this.cube.attributeTypes[parts[parts.length - 2]];
        return type === "NUMBER" ? parseInt(value) : value;
    }

    /**
     * set the filter value without notifying filter change
     * @param {string} filterName
     * @param value
     */
    private setInternalFilterValue(filterName: string, value: any): boolean {
        let internalFilterValue: any = this.internalFilter[filterName];

        if (value === internalFilterValue) {
            return false;
        }

        // if the current value is a SearchControlSelectObjects only sets the internal value to the ids
        if (this.internalFilter[filterName] instanceof SearchControlSelectObjects) {
            if (!this.internalFilter[filterName].notifyChange) {
                this.internalFilter[filterName] = this.internalFilter[filterName].objects.map((object) => object.id);
                internalFilterValue = this.internalFilter[filterName];
            }
        }

        if (Array.isArray(value) && Array.isArray(internalFilterValue) && this.match(value, internalFilterValue)) {
            if (this.internalFilter[filterName] instanceof SearchControlSelectObjects) {
                this.internalFilter[filterName] = value;
            }
            return false;
        }

        this.internalFilter[filterName] = value;

        this.filterFieldChange.emit(new FilterValueChangedEvent(filterName, value));

        if (filterName === FilterModel.DATE_RANGE_FILTER_NAME) {
            this.dateRange = value;
        } else {
            if (value == undefined || (Array.isArray(value) && value.length == 0)) {
                this.dimensionFilterMembers.delete(filterName);
            } else {
                this.dimensionFilterMembers.set(filterName, this.transformValuesToMembers(filterName, value));
            }
        }

        return true;
    }

    match(s1: string[], s2: string[]): boolean {
        if (!s1) {
            s1 = [];
        }

        if (!s2) {
            s2 = [];
        }

        if (s1 === s2) {
            return true;
        }


        if (s1.length !== s2.length) {
            return false;
        }

        if (s1.length === 0) {
            return true;
        }

        return !s1.some((s: string, index: number) => s !== s2[index]);
    }
}
